import { ServerExecutor } from './ServerExecutor.js';
import { UiCommandRunner } from '../../core/imcmd/UiCommandRunner.js';
import { UiCommandRegistry } from '../../core/imcmd/UiCommandRegistry.js';
import { ServerCmdProviderRegistry } from '../../core/cmd/ServerCmdProviderRegistry.js';
import { ServerResponseCmdAdapter } from '../../core/cmd/ServerResponseCmdAdapter.js';
import { ImSys } from '../../core/ImSys.js';
import { Loc } from '../../locales/registry.js';
import { ServerCommand } from '../../core/cmd/ServerCommand.js';

/**
 * Фасад для View: отправляет запросы (через ServerExecutor)
 * и применяет ответы (UI/ServerCmd адаптеры), сохраняя семантику
 * callback/onerror как в старом doJson/doResponse.
 */
class ViewServerExecutor {
    constructor(view, transport) {
        this._view = view;
        this._transport = transport ?? new ServerExecutor();
    }
    /**
     * Унифицированный вызов серверной команды с поддержкой callback/onerror.
     * @param req       запрос к серверу (с произвольными доп. полями)
     * @param callback  вызывается при явной команде "callback" или один раз в конце,
     *                  если не было ошибок и явных callback-команд
     * @param onerror   вызывается при ошибке или при команде "error"
     * @param setBusy   управлять индикатором занятости на время запроса (по умолчанию true)
     * @returns Promise, который завершается после обработки ответа (callback/onerror уже вызваны).
     *          2026-10-15: нужен очереди области фонового сохранения (BgSaveScope).
     */
    doJson(req, callback, onerror, setBusy = false) {
        const hooks = {
            onBusy: (on) => {
                if (!setBusy)
                    return;
                on ? this._view.modeBusy() : this._view.modeNormal();
            },
            onResponse: (cmds) => {
                this._handleResponse(cmds, req, callback, onerror);
            },
            onError: (err, cmds) => {
                // если сервер прислал команды даже в ошибке — применим их (без финального callback)
                if (cmds?.length)
                    this._handleResponse(cmds, req, undefined, undefined);
                // базовая совместимая обработка ошибки (сообщения/логика notallowed — на стороне View/имсистемы)
                try {
                    const msg = err?.msg;
                    const type = err?.type;
                    if (type === "system") {
                        console.error(msg ?? err);
                        ImSys.alert(Loc.error, Loc.systemError);
                    }
                    else if (msg) {
                        if (type !== "notallowed") {
                            ImSys.alert("", msg);
                        }
                        else {
                            const has = document.querySelector("im-dialog.notallowed");
                            if (!has) {
                                ImSys.alert(Loc.error, msg, undefined, err.error, "notallowed");
                            }
                        }
                    }
                }
                finally {
                    onerror?.(err);
                }
            }
        };
        const context = {
            siteKey: ImSys.siteKey,
            pageKey: ImSys.pageKey,
            viewConfig: this._view.options.viewCfg,
            lang: ImSys.lang,
            uiLang: ImSys.uiLang,
            location: window.location.href
        };
        // Запуск транспорта (возвращает Promise — можно при необходимости await-ить снаружи)
        return this._transport
            .execute(req, context, hooks)
            .catch((e) => {
            // safety net
            console.error("[ViewServerExecutor] transport error", e);
            this._view.modeNormal();
            onerror?.(e);
        });
    }
    /**
     * Применяет массив серверных команд и реализует семантику callback/onerror,
     * как это делал легаси doResponse.
     */
    _handleResponse(cmds, _req, callback, onerror) {
        this._view.modeNormal();
        let hasError = false;
        let callbackExecuted = false;
        // адаптер для «обычных» команд (поддерживаемый набор — в регистрах)
        const uiRunner = new UiCommandRunner(UiCommandRegistry.uiCommands);
        const adapter = new ServerResponseCmdAdapter(uiRunner, ServerCmdProviderRegistry.serverCommands);
        const ctx = {
            viewHost: this._view.element,
            sourceEl: this._view.element,
            uiNotMask: this._view.getUiNotMask?.() ?? ""
        };
        try {
            for (const cmd of (cmds ?? [])) {
                switch (cmd.type) {
                    case ServerCommand.error: {
                        hasError = true;
                        ImSys.alert(Loc.error, cmd.msg ?? Loc.systemError);
                        onerror?.(cmd);
                        break;
                    }
                    case ServerCommand.callback: {
                        callbackExecuted = true;
                        callback?.(cmd);
                        break;
                    }
                    default: {
                        if (cmd.isCallback) {
                            callbackExecuted = true;
                            callback?.(cmd);
                        }
                        else {
                            try {
                                adapter.run([cmd], ctx);
                            }
                            catch (e) {
                                console.error("[ViewServerExecutor] UI/ServerCmd apply error:", e, cmd);
                                // не валим весь поток из-за одной команды
                            }
                        }
                        break;
                    }
                }
            }
            // Совместимость: если не было ошибок и явного callback — вызвать cb() один раз
            if (!hasError && !callbackExecuted) {
                callback?.();
            }
        }
        catch (e) {
            console.error("[ViewServerExecutor] Response handling failed:", e);
            console.trace(e);
            onerror?.(e);
        }
    }
    /**
     * Выполнить одну серверную команду (перенос execServerCmd из View).
     * Возвращает true, если команда была успешно адаптирована и выполнена.
     */
    execServerCmd(cmd, uiNotMask) {
        if (!cmd)
            return false;
        const ctx = {
            viewHost: this._view.element,
            sourceEl: this._view.element,
            uiNotMask: uiNotMask ?? this._view.getUiNotMask?.() ?? ""
        };
        const uiRunner = new UiCommandRunner(UiCommandRegistry.uiCommands);
        const adapter = new ServerResponseCmdAdapter(uiRunner, ServerCmdProviderRegistry.serverCommands);
        try {
            const executed = adapter.run([cmd], ctx);
            return !!executed;
        }
        catch (e) {
            console.error("[ViewServerExecutor.execServerCmd] Ошибка:", e);
            return false;
        }
    }
}

export { ViewServerExecutor };
